import { useCallback, useEffect, useRef, useState } from 'react';

import { SSE } from 'sse.js';

import { KimMessageType, StreamBlock } from '../../../../backend/src/types/types';
import { APIBaseChronos } from '@/api/hosts';
import useGetFetchConfig from '@/api/useGetFetchConfig';

export interface KimStreamChunk {
  type: KimMessageType;
  content: StreamBlock;
  timestamp: number;
}

interface UseKimStreamProps {
  matterId: string;
  subsetId: string;
  threadId: string;
  enabled?: boolean;
  initialLastMessageId?: string;
}

export const useKimStream = ({
  matterId,
  subsetId,
  threadId,
  enabled = true,
  initialLastMessageId,
}: UseKimStreamProps) => {
  const [chunks, setChunks] = useState<KimStreamChunk[]>([]);
  const [isConnected, setIsConnected] = useState(false);
  const sseRef = useRef<SSE | null>(null);
  const lastMessageIdRef = useRef<string | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMountedRef = useRef(true);
  const { fetchConfigGET } = useGetFetchConfig();

  const buildUrl = useCallback(
    (lastMessageId: string | null) => {
      const base = `${APIBaseChronos}/api/matter/${matterId}/subset/${subsetId}/kim/threads/${threadId}/stream`;
      if (lastMessageId) {
        return `${base}?lastMessageId=${encodeURIComponent(lastMessageId)}`;
      }
      return base;
    },
    [matterId, subsetId, threadId],
  );

  const connect = useCallback(() => {
    if (!isMountedRef.current || !enabled) return;

    // Close any existing connection
    if (sseRef.current) {
      sseRef.current.close();
      sseRef.current = null;
    }

    const url = buildUrl(lastMessageIdRef.current);

    try {
      const eventSource = new SSE(url, {
        headers: Object.fromEntries(fetchConfigGET.headers.entries()),
      });

      eventSource.onmessage = (event: any) => {
        if (!isMountedRef.current) return;

        try {
          if (!event.data || !event.data.trim()) return;

          const data = JSON.parse(event.data);
          lastMessageIdRef.current = String(data.timestamp);
          setChunks((prev) => [...prev, data as KimStreamChunk]);
        } catch (error) {
          console.error('Failed to parse Kim stream event:', error);
        }
      };

      eventSource.onopen = () => {
        if (isMountedRef.current) {
          setIsConnected(true);
        }
      };

      eventSource.onerror = () => {
        if (!isMountedRef.current) return;

        setIsConnected(false);

        // Auto-reconnect with lastMessageId after a short delay
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
        reconnectTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current && enabled) {
            connect();
          }
        }, 2000);
      };

      sseRef.current = eventSource;
    } catch (error) {
      console.error('Failed to setup Kim stream event source:', error);
    }
  }, [enabled, buildUrl, fetchConfigGET.headers]);

  useEffect(() => {
    isMountedRef.current = true;
    setChunks([]);
    lastMessageIdRef.current = initialLastMessageId ?? null;

    if (enabled && matterId && subsetId && threadId) {
      connect();
    }

    return () => {
      isMountedRef.current = false;
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      if (sseRef.current) {
        sseRef.current.close();
        sseRef.current = null;
      }
      setIsConnected(false);
    };
  }, [enabled, matterId, subsetId, threadId, connect, initialLastMessageId]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (sseRef.current) {
      sseRef.current.close();
      sseRef.current = null;
    }
    setIsConnected(false);
  }, []);

  const reset = useCallback(() => {
    disconnect();
    lastMessageIdRef.current = null;
    setChunks([]);
  }, [disconnect]);

  const content = chunks
    .map((c) => {
      if (!c.content) return `[no content field - keys: ${Object.keys(c).join(',')}]`;

      if (c.content.type === 'text') return c.content.value;
      if (c.content.type === 'start') return '';
      if (c.content.type === 'end') return '';
      if (c.content.type === 'reference') return `[${c.content.type}:${c.content.id}]`;
    })
    .join(' ');

  return { chunks, content, isConnected, disconnect, reset };
};
